import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from "react-icons/fa";
import AppContext from '../contexts/AppContext';
import LeafNode from '../components/services/LeafNode';
import NonLeafNode from '../components/services/NonLeafNode';
import InformModal from '../components/utils/InformModal';
import ConfirmModal from '../components/utils/ConfirmModal';
import FloatingFooter from '../components/utils/FloatingFooter';

const buildComparableDetail = (value) => {
  if (!value) return null;

  return {
    isLeaf: value.isLeaf ?? false,
    layoutStyle: value.layoutStyle ?? '',
    name: value.name ?? '',
    description: value.description ?? '',
    bannerImage: value.bannerImage ?? '',
    iconImage: value.iconImage ?? '',
    carouselImages: value.carouselImages ?? [],
    mainText: value.mainText ?? '',
    contact: value.contact ?? {},
    geoLocation: value.geoLocation ?? {},
    searchTags: value.searchTags ?? '',
    externalUrl: value.externalUrl ?? null,
  };
};

const Detail = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const location = useLocation(); // 获取当前 URL 的路径
  const navigate = useNavigate();
  const { token, content, uploadImage, userInfo, selectedRole } = useContext(AppContext);
  const [detail, setDetail] = useState(null);
  const [initialComparableDetail, setInitialComparableDetail] = useState(null);
  const isNew = location.pathname.includes('/add') ? (location.pathname.includes('/add/list') ? "list" : "entry") : "old";//糟糕的三元嵌套 isNew 修改为 list entry old三种状态
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  const [saveMode, setSaveMode] = useState(null)

  const applyDetailSnapshot = (nextDetail) => {
    setDetail(nextDetail);
    setInitialComparableDetail(buildComparableDetail(nextDetail));
  };

  useEffect(() => {
    if (!state?.content && id && token) {
      const fetchContent = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/content/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            applyDetailSnapshot(data);
          }
        } catch (error) {
          console.error('Failed to fetch content:', error);
        }
      };
      fetchContent();
    }
  }, [state?.content, id, token]);
  /* 先用 location.state，如果没有再拉接口 */
  useEffect(() => {
    if (state?.content) {
      applyDetailSnapshot(state.content);
      return;
    }
  }, [state?.content, id, token]);

  useEffect(() => {
    // if (!isNew) {
    if (isNew === 'old') {
      // For "old" mode, utalise its origin detail
      const findNodeById = (id, node) => {
        const queue = [node];
        while (queue.length > 0) {
          const currentNode = queue.shift();
          if (currentNode.id === id) return currentNode;
          if (currentNode.attributes) {
            queue.push(...currentNode.attributes);
          }
        }
        return null;
      };

      if (content) {
        const node = findNodeById(id, content);
        applyDetailSnapshot(node);
      }
    } else {
      // For "new" mode, initialize a blank detail, separate list or entry
      if (isNew === 'entry') {
        // entry initial set
        applyDetailSnapshot({
          name: '',
          description: '',
          isLeaf: true,  // Or false, depending on the type of content being created
          layoutStyle: 'none',
          content_parent_id: id,
          mainText: '',
          carouselImagePublic: [],
          carouselImagePrivate: [],
          geoLocation: {}
        });
      } else {
        // list initial set
        applyDetailSnapshot({
          name: '',
          description: '',
          isLeaf: false,  // Or false, depending on the type of content being created
          layoutStyle: 'overlay',
          content_parent_id: id,
        });
      }
    }
  }, [id, content, isNew]);

  useEffect(() => {
    if (!isModalOpen || saveMode === "exit") return undefined;

    const timer = setTimeout(() => {
      setIsModalOpen(false);
      setSaveMode(null);
    }, 1200);

    return () => clearTimeout(timer);
  }, [isModalOpen, saveMode]);

  const hasUnsavedChanges = useMemo(() => {
    if (!detail || !initialComparableDetail) return false;

    return JSON.stringify(buildComparableDetail(detail)) !== JSON.stringify(initialComparableDetail);
  }, [detail, initialComparableDetail]);

  if (!detail) return <div>Loading...</div>;

  const updateDetail = (valueOrUpdater) => {
    setDetail((prevDetail) => (
      typeof valueOrUpdater === 'function' ? valueOrUpdater(prevDetail) : valueOrUpdater
    ));
  };

  const handleInputChange = (e, arrayName, index) => {
    if (arrayName) {
      const value = e ? e.target.value : '';
      setDetail((prevDetail) => ({
        ...prevDetail,
        [arrayName]: prevDetail[arrayName].map((item, i) => (i === index ? value : item)),
      }));
    } else {
      const { name, value } = e.target;
      setDetail((prevDetail) => ({
        ...prevDetail,
        [name]: value,
      }));
    }
  };

  const handleImageChange = async (e, width, height) => {
    const file = e.target.files[0];
    const imagePublicAddress = await uploadImage(file, width, height);
    const name = e.target.name;
    setDetail((prevDetail) => ({
      ...prevDetail,
      [name]: imagePublicAddress,
    }));
  };

  const handleImageDelete = (name) => {
    setDetail((prevDetail) => ({
      ...prevDetail,
      [name]: '',
    }));
  };

  const saveContent = async () => {
    try {
      const apiUrl = isNew !== "old"
        ? `${process.env.REACT_APP_API_BASE_URL}/private/content/add`
        : `${process.env.REACT_APP_API_BASE_URL}/private/content/edit`;

      const requestBody = isNew !== "old"
        ? {
          email: userInfo.user.email,
          venue_id: selectedRole.id,
          content_parent_id: detail.content_parent_id,
          isLeaf: detail.isLeaf,
          layoutStyle: detail.layoutStyle,
          name: detail.name,
          description: detail.description,
          bannerImage: detail.bannerImage,
          iconImage: detail.iconImage,
          carouselImages: detail.carouselImages,
          mainText: detail.mainText,
          contact: detail.contact,
          geoLocation: detail.geoLocation,
          searchTags: detail.searchTags,
          externalUrl: detail.externalUrl
        }
        : {
          email: userInfo.user.email,
          venue_id: selectedRole.id,
          content_id: detail.id,
          isLeaf: detail.isLeaf,
          layoutStyle: detail.layoutStyle,
          name: detail.name,
          description: detail.description,
          bannerImage: detail.bannerImage,
          iconImage: detail.iconImage,
          carouselImages: detail.carouselImages,
          mainText: detail.mainText,
          contact: detail.contact,
          geoLocation: detail.geoLocation,
          searchTags: detail.searchTags,
          externalUrl: detail.externalUrl
        };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        setInitialComparableDetail(buildComparableDetail(detail));
        if (saveMode === "exit") {
          setSaveMode(null);
          navigate(-1);
          return data.fileUrl;
        }
        setIsModalOpen(true);
        return data.fileUrl;
      } else {
        setIsErrorModalOpen(true);
        throw new Error('Failed to save content');
      }
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  return (
    <div className="w-[90%] max-w-7xl mx-auto pb-20">
      <div className="flex items-center gap-3 py-4">
        <FaArrowLeft
          className={`h-6 w-6 cursor-pointer text-[#334154] transition-all duration-200 hover:-translate-x-0.5 hover:text-[#172033] ${selectedRole.type === 'adv' ? 'hidden' : ''}`}
          onClick={() => {
            if (hasUnsavedChanges) {
              setIsLeaveConfirmOpen(true);
              return;
            }
            navigate(-1);
          }}
        />
        <div className="hidden text-[2rem] font-semibold tracking-[-0.03em] text-[#2557d6] md:block">
          {isNew !== "old" ? 'Add Content' : detail.isLeaf ? 'Directory Entry' : 'Directory List'}
        </div>
      </div>
      <div className="rounded-[28px] border border-[#e2e7f6] bg-white p-5 shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
        {detail.isLeaf ? (
          <LeafNode
            detail={detail}
            saveContent={saveContent}
            handleImageChange={handleImageChange}
            handleInputChange={handleInputChange}
            handleImageDelete={handleImageDelete}
            setDetail={updateDetail}
            canSwitch={selectedRole.type !== 'adv'}
          />
        ) : (
          <NonLeafNode
            detail={detail}
            saveContent={saveContent}
            handleImageChange={handleImageChange}
            handleInputChange={handleInputChange}
            handleImageDelete={handleImageDelete}
            setDetail={updateDetail}
          />
        )}
      </div>
      <FloatingFooter hasChanges={hasUnsavedChanges} multipleSave={true}
        onSaveContinue={() => { setIsCheckModalOpen(true); setSaveMode("continue"); }}
        onSaveExit={() => { setIsCheckModalOpen(true); setSaveMode("exit"); }} />
      <InformModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSaveMode(null); }}
        content={isNew ? "Successfully Added!" : "Successfully Edited!"}
        type='inform'
      />
      <InformModal
        isOpen={isErrorModalOpen}
        onClose={() => { setIsErrorModalOpen(false); }}
        content="Oops! Something went wrong, check again"
        type='alert'
      />
      <ConfirmModal
        isOpen={isCheckModalOpen}
        onClose={() => { setIsCheckModalOpen(false) }}
        onConfirm={() => { saveContent(); setIsCheckModalOpen(false); }}
        header="Confirm Save"
        content="Are you sure you want to save the changes?" />
      <ConfirmModal
        isOpen={isLeaveConfirmOpen}
        onClose={() => setIsLeaveConfirmOpen(false)}
        onConfirm={() => navigate(-1)}
        header="Unsaved Changes"
        content="Changes will be lost if you leave this page without saving. Do you want to continue?"
      />
    </div>

  );
};

export default Detail;
